import { siteConfig } from '@/config/site'

/* The FAQ page's questions and answers (FaqPage), and its FAQPage structured data. Each answer is
   written to stand on its own, because answer engines quote one at a time. Keep `id`s stable once
   published: they are the links to each question (/faq#deal). */

export type FaqItem = {
  id: string
  question: string
  /** Paragraphs of plain text. The site's email address becomes a link. */
  answer: string[]
  /** Ends the answer with Book a call. */
  bookCall?: boolean
}

export type FaqGroup = {
  id: string
  title: string
  items: FaqItem[]
}

export const faqUpdated = '19 October 2026'

export const faqGroups: FaqGroup[] = [
  {
    id: 'about-united',
    title: 'About United',
    items: [
      {
        id: 'what-is-united',
        question: 'What is United?',
        answer: [
          'United partners with accounting firms to change how they produce their work. We buy into your firm, move its production onto Justine, our platform of AI agents, and help it grow by buying client books into it. You keep running it, with your name, your team and your clients. The aim is to build, with hundreds of firms, the financial platform American small business runs on, with the accountant still the trusted adviser at the center of it.',
        ],
      },
      {
        id: 'private-equity',
        question: 'Is United private equity, a roll-up, or a broker?',
        answer: [
          'No. United buys into accounting firms to transform them, not to consolidate them. Private equity buys firms to consolidate and cut. Software vendors sell tools and leave the work, the risk and the client to you. We do the work with you, and the accountant signs. You keep part of your firm and keep leading it, and when books are bought, they are bought into your firm. It becomes the consolidator rather than the consolidated.',
        ],
      },
      {
        id: 'software-or-firm',
        question: 'Is United a software company or an accounting firm?',
        answer: [
          'Both. United builds software and buys into accounting firms, and neither half works without the other. We don’t sell you software, and we don’t buy you to consolidate you. We buy into your firm and transform how it produces, because you can’t rebuild how a practice works from outside it. Justine gets better by running on real client books, and everything we build for one firm becomes part of the platform for every firm in United.',
        ],
      },
      {
        id: 'team',
        question: 'Who is behind United?',
        answer: [
          'United was founded in September 2025, is venture-backed, and has teams in New York and Estonia. Between them, its three founders have built a professional-services network, a buy-and-build platform of professional practices, and AI production systems for accounting.',
          'Karl Paadam, CEO, joined PwC at 26 as founding shareholder of a new practice with no clients, no revenue and no team, and within five years built it into Estonia’s largest Tax & Legal practice. Over ten years as a partner he led acquisitions and integrations across Central and Eastern Europe. He has built and exited two technology companies.',
          'Pascal Noth, COO, spent two decades in private equity turning founder-led businesses into platforms, at Palamon Capital Partners and at Partners Group, where he was Head of Private Equity Healthcare & Life Sciences. His investments include Ober Scharrer Group, which became Germany’s largest ophthalmology group, and Zabka, which grew from 3,000 to around 11,000 franchisees.',
          'Peter Potapov, CTO, led engineering and machine learning at Osome and led the build of its accounting production system, on which about 200 bookkeepers served more than 15,000 clients. He then led technology at Kick, a US self-driving bookkeeping company backed by the OpenAI Startup Fund, General Catalyst, Felicis and GV.',
        ],
      },
    ],
  },
  {
    id: 'the-partnership',
    title: 'The partnership',
    items: [
      {
        id: 'deal',
        question: 'How does a partnership with United work?',
        answer: [
          'United buys 60% of your firm. You keep 40% and continue to lead it. For the 60%, you are paid cash at close and deferred consideration over several years, earned against retention and what we build together. Exact terms depend on your firm and are agreed before you join.',
        ],
      },
      {
        id: 'still-my-firm',
        question: 'Is it still my firm after joining United?',
        answer: [
          'Yes. Owners who partner with United keep 40% of their firm and continue to lead it. It keeps its name, its team and its client relationships. What changes is underneath: engineers and product people building around how your firm works, production moving onto Justine, capital and a deal team to buy books into your firm, and back-office support. At Ridge Paragon, our first firm, the clients stayed and Doug continues to lead it.',
        ],
      },
    ],
  },
  {
    id: 'unitedos',
    title: 'Justine',
    items: [
      {
        id: 'what-unitedos-does',
        question: 'What does Justine actually do?',
        answer: [
          'Justine (formerly UnitedOS), United’s platform, is practice management with AI agents that take on accounting production, one workflow at a time, starting with bookkeeping. Client relationships, professional judgment and strategy stay with your accountants.',
          'The monthly close agent runs today. It categorizes transactions, reconciles accounts, works receivables and payables, reconciles payroll, calculates cost of goods sold and reviews the financial statements, then hands the accountant a finished draft to review and sign. It runs on QuickBooks Online, alongside the tools your team already uses.',
          'Calculations run on fixed rules, every exception goes to an accountant, and nothing reaches a client without an accountant’s approval.',
          'The agent copies. The human decides.',
        ],
      },
      {
        id: 'unitedos-next',
        question: 'What will Justine do next?',
        answer: [
          'Next, Justine will collect documents and data from your clients and chase what’s missing, which is where most firms lose the most time. Then it will draft client communication in your accountants’ voice. Tax preparation comes through partners and existing tools where they work.',
        ],
      },
      {
        id: 'vs-software',
        question: 'How is Justine different from software I could just buy?',
        answer: [
          'Software makes individual tasks faster but leaves the workflow to you: collecting missing information, moving data between systems, managing handoffs, checking the result, and fitting each new tool into all of that. United changes how the whole workflow runs, inside your firm. And what we build stays in the platform, so every firm that joins after you makes it better for you, and you make it better for them.',
        ],
      },
      {
        id: 'change-software',
        question: 'Do I have to change my software to use Justine?',
        answer: [
          'Mostly not. Justine runs as your firm’s practice management system, alongside your existing accounting software, and the close agent runs on QuickBooks Online. Where an off-the-shelf tool already does something well, we use it rather than reinvent it. What does change in the first 90 days is how the firm is run: pricing, billing, collections and financial control move onto one operating standard, United Standard.',
        ],
      },
      {
        id: 'after-joining',
        question: 'What happens after a firm joins United?',
        answer: [
          'In the first 90 days, before any technology, your firm moves onto United Standard: finance, data, billing, people and the client announcement. Then production moves onto Justine one service line at a time. We map every workflow in your firm, measure where the hours actually go, by person and by client, and automate the biggest bottleneck first, with your reviewers approving every output. Then the next bottleneck, same method. You and your team work with our engineers every week on what gets built.',
          'At Ridge Paragon the bottleneck was the monthly close, so that’s what we built first.',
        ],
      },
    ],
  },
  {
    id: 'your-firm-and-team',
    title: 'Your firm and your team',
    items: [
      {
        id: 'your-team',
        question: 'What happens to my team when my firm joins United?',
        answer: [
          'Your team stays, and its work changes. Production moves to AI agents, and your people keep the client relationships and move from production into review and advice. If part of your production runs offshore, that’s the first work to move. Back-office services such as payroll, HR, IT, compliance reporting and bookkeeping quality assurance move to the center so they stop landing on your desk, and we plug in recruiting when you genuinely need to hire.',
        ],
      },
      {
        id: 'your-clients',
        question: 'My clients came to me. Will they stay?',
        answer: [
          'Your clients see the same name, the same people and the same relationship; the partnership is announced to them as part of joining. What changes is how quickly you get back to them and how much more you can do for them. When Ridge Paragon joined United in April 2026, its clients stayed.',
        ],
      },
      {
        id: 'hiring',
        question: 'What if I can’t hire the middle layer I need?',
        answer: [
          'Most firms can’t. Justine takes on the production volume that made you need those people, your team moves up into review, and our recruiting helps when you actually do need to hire. You stop being the only reviewer in the building.',
        ],
      },
    ],
  },
  {
    id: 'growth',
    title: 'Growth',
    items: [
      {
        id: 'buying-firms',
        question: 'Will United help my firm buy other firms?',
        answer: [
          'Yes. Buying client books into your firm is a core part of a United partnership. Tell us which client books would make your firm stronger. Our team finds, finances and closes them, and your firm absorbs them on Justine. Private equity groups are consolidating the market; with us, your firm does the buying.',
        ],
      },
      {
        id: 'new-clients',
        question: 'Can United help me get new clients?',
        answer: [
          'Yes, as part of a United partnership rather than as a separate service. With capacity freed up, we help you win new clients and do more for existing ones with modern, digital marketing, and we buy books into your firm. Clients rarely switch accountants, so we build for steady growth rather than promise it.',
        ],
      },
    ],
  },
  {
    id: 'fit',
    title: 'Fit',
    items: [
      {
        id: 'own-automation',
        question: 'I’ve already built my own automation. What does United add?',
        answer: [
          'Then you’re exactly the kind of person we’re looking for. Here’s the ceiling you hit alone: everything built on rented tools stays rented. You pay for the model, the costs keep rising, and the context never becomes yours. What compounds for us isn’t feedback, it’s judgment, and it stays in the network.',
          'And there are parts nobody builds alone at any level of skill: the capital and deal team to buy books into your firm, and financial products for your clients, which are licensing problems rather than technical ones.',
        ],
      },
      {
        id: 'build-it-myself',
        question: 'Why not build the automation myself?',
        answer: [
          'You probably could build the first piece, and that’s most of the reason we’re talking to you rather than the firm next door. Automating one task is the easy part. The hard part is automation that runs a whole workflow end to end, every month, on messy data, against real deadlines, with an accountant’s name on the result. An AI model straight out of the box can’t be trusted with a client’s books. It can once it has context about each client, workflows rebuilt around it, and guardrails that send every exception to an accountant. That’s what takes our close agent from raw transactions to a finished draft the accountant reviews and signs.',
          'A demo is easy. Production that holds up across hundreds of books is hard, and our engineers have done it before: Peter Potapov, our CTO, has built accounting production systems twice, before and after large language models. Doing it alone means becoming the engineer, the owner and the reviewer at once, on nights and weekends, permanently, because the tooling changes every few months. With us you build it with an engineering team behind you, and every firm on the platform makes it better.',
        ],
      },
      {
        id: 'sell-later',
        question: 'Why not keep growing my firm and sell it later?',
        answer: [
          'Your trajectory is real and we won’t argue with it. But you’ll grow it and it will still be priced like a small firm, because the buyer will be pricing a small firm. And the market is likely to look very different within a few years: once production runs on software, routine work gets cheaper, firms that haven’t changed will be worth less, and technology, data and capital will concentrate in a few large networks. An owner who joins now does it while the firm is valued on what it earns today, and helps build what the market will reward later.',
        ],
      },
      {
        id: 'why-now',
        question: 'My firm is growing fast. Why join United now?',
        answer: [
          'Three reasons. AI is now good enough to run production, if you know how. Client books are coming to market as a generation of owners retires, and a firm whose production runs on AI has the capacity to absorb them. And we’re choosing a small group of owners before our Series A, while most of what we plan to build is still ahead. Waiting grows your firm. Joining now does that and more.',
        ],
      },
      {
        id: 'buy-technology',
        question: 'Can I buy Justine on its own, or hire United as consultants?',
        answer: [
          'Not today. Justine comes with the partnership, because the value is the combination: technology, back office, acquisitions and ownership.',
        ],
      },
      {
        id: 'dont-want-to-grow',
        question: 'What if I don’t want to grow my firm?',
        answer: [
          'Then United isn’t the right fit, and there’s nothing wrong with that. If a peer group of owners at your level would be useful, that we do have.',
        ],
      },
      {
        id: 'first-step',
        question: 'What’s the first step to partnering with United?',
        answer: [
          `A conversation about your firm. Book a call, or email ${siteConfig.email}, and tell us how your firm works today and what you want it to become. The first call goes both ways: we learn about your firm, and we explain United in more detail. If we both see a fit, you meet the team building Justine and see how it would apply to your firm’s own work.`,
        ],
        bookCall: true,
      },
    ],
  },
]

/** Every question, in page order. */
export const faqItems = faqGroups.flatMap((group) => group.items)
